import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Keyboard } from "swiper/modules";
import { PropertyPhoto } from "@cma/presentation-generation-logic/models";
import { ResponsiveImage } from "../../../shared/ResponsiveImage";
import EditIcon from "@mui/icons-material/Edit";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { forwardRef, useState } from "react";
import type { SwiperRef } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import {
  GalleryImage,
  useImageGalleryStore,
} from "@cma/store/image-gallery.store";
import styled from "@emotion/styled";
import {
  Box,
  Typography,
  useBreakpoint,
  IconButton,
} from "@avenue-8/sidekick-ui";
import ImageIcon from "@mui/icons-material/Image";

const GalleryImageContainer = styled.div`
  position: relative;
  width: 100%;
  cursor: pointer;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${(p) => p.theme.colors.background2};

  &[aria-disabled="true"] {
    opacity: 0.5;
    pointer-events: none;
    cursor: default;
  }

  &:hover {
    img {
      opacity: 0.8;
      transition: all 0.2s ease-in-out;
      transform: scale(1.1);
    }
  }
`;

const NoImageContainer = styled(GalleryImageContainer)`
  gap: 0.25rem;
`;

const SwiperContainer = styled(Box)`
  overflow: visible;
  position: relative;
  margin-top: 3rem;

  & .swiper-pagination-bullet {
    margin: 0 30px !important;
  }

  & .swiper-pagination-bullet-active {
    background-color: ${(p) => p.theme.colors.primary};
  }

  .swiper-slide {
    max-height: 180px;
    border-radius: 0.5rem;
    overflow: hidden;
    position: relative;

    img {
      width: 100%;
      height: 180px;
      object-fit: cover;
    }
  }
`;

const NavButton = styled(IconButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background-color: ${(p) => p.theme.colors.background};
`;

const PrevButton = styled(NavButton)`
  left: 0.5rem;
`;

const NextButton = styled(NavButton)`
  right: 0.5rem;
`;

const GalleryIconContainer = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  border-radius: 50%;
  background-color: ${(p) => p.theme.colors.background};
  color: ${(p) => p.theme.text.colors.primary};
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type Props = {
  photos?: PropertyPhoto[];
  displayMode?: string;
  editMode?: boolean;
  onClickImage?: (index: number) => void;
};

const isTempImage = (id: string) => {
  return id?.startsWith("temp-");
};

export const ImagesCarousel = forwardRef<SwiperRef, Props>((props, ref) => {
  const { photos, displayMode, onClickImage, editMode = false } = props;
  const setGalleryImages = useImageGalleryStore(
    (store) => store.setGalleryImages,
  );
  const galleryPhotos: GalleryImage[] = (photos || []).map((photo, i) => ({
    id: photo.id,
    url: photo.url,
    alt: photo.caption || `Property photo ${i}`,
  }));

  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const isMobile = useBreakpoint("mobile");

  const updateNavState = (s: SwiperType) => {
    setIsBeginning(s.isBeginning);
    setIsEnd(s.isEnd);
  };

  return (
    <SwiperContainer onClick={(e) => e.stopPropagation()}>
      {!isBeginning && (
        <PrevButton size="small" onClick={() => swiper?.slidePrev()}>
          <ChevronLeftIcon fontSize="small" />
        </PrevButton>
      )}
      {!isEnd && (
        <NextButton size="small" onClick={() => swiper?.slideNext()}>
          <ChevronRightIcon fontSize="small" />
        </NextButton>
      )}
      <Swiper
        ref={ref}
        onSwiper={(s) => {
          setSwiper(s);
          updateNavState(s);
        }}
        onSlideChange={updateNavState}
        spaceBetween={16}
        modules={[Navigation, Pagination, Keyboard]}
        observer={true}
        observeParents={true}
        resizeObserver={true}
        updateOnWindowResize
        grabCursor
        mousewheel
        slidesPerView={
          displayMode === "mobile" || isMobile ? 1.3 : editMode ? 2.4 : 3.4
        }
        keyboard={{ enabled: true, onlyInViewport: true }}
      >
        {photos?.map(({ caption, url, id }, index) => {
          return (
            <SwiperSlide virtualIndex={index} key={id ?? url}>
              <GalleryImageContainer
                aria-disabled={isTempImage(id)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isTempImage(id)) return;
                  onClickImage?.(index);
                }}
              >
                <ResponsiveImage alt={caption ?? ""} src={url} size="sm" />
                {editMode && (
                  <GalleryIconContainer>
                    <EditIcon sx={{ fontSize: 12 }} />
                  </GalleryIconContainer>
                )}
              </GalleryImageContainer>
            </SwiperSlide>
          );
        })}
        {editMode &&
          (!photos ||
            (photos.length === 0 &&
              [1, 2, 3].map((_, index) => (
                <SwiperSlide virtualIndex={index} key={index}>
                  <NoImageContainer
                    onClick={(e) => {
                      e.stopPropagation();
                      setGalleryImages({
                        images: galleryPhotos,
                        initialImageIndex: index,
                      });
                    }}
                  >
                    <ImageIcon color="secondary" />
                    <Typography variant="body2">No photo</Typography>
                  </NoImageContainer>
                </SwiperSlide>
              ))))}
      </Swiper>
    </SwiperContainer>
  );
});

ImagesCarousel.displayName = "ImagesCarousel";
